import { HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { EMPTY, from, pipe, switchMap, filter, tap } from "rxjs";

import { AlertStore } from "@alert/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";

import { CloseProfileSubscriptionPayload, ProfileSubscriptionResponse, ProfileSubscriptionListItem, RenewProfileSubscriptionPayload, AddPauseProfileSubscriptionPayload, UpdatePauseProfileSubscriptionPayload, DeletePauseProfileSubscriptionPayload, ProfileSubscriptionPause, SwitchProfileSubscriptionPayload, TransferProfileSubscriptionPayload } from "./entities/profile-subscription.model";
import { ProfileSubscriptionService } from "./infra/profile-subscription.service";
import { SelfUserStore, PermissionNumber } from '@person/user/self/data-access';
import { Router } from "@angular/router";
const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

export interface IProfileSubscriptionStore {
  personId: string | null;
  lastCreatedSubscription: ProfileSubscriptionResponse | null;
  lastUpdatedSubscription: ProfileSubscriptionResponse | null;
  lastPatchedSubscription: ProfileSubscriptionResponse | null;
  lastRenewedSubscription: ProfileSubscriptionResponse | null;
  lastClosedSubscription: ProfileSubscriptionResponse | null;
  lastPausedSubscription: ProfileSubscriptionResponse | null;
  lastSwitchedSubscription: ProfileSubscriptionResponse | null;
  lastTransferredSubscription: ProfileSubscriptionResponse | null;
  currentSubscription: ProfileSubscriptionResponse | null;
  lastDeletedSubscriptionId: string | null;
  lastDeletedSubscriptionRenewalsId: string | null;
  subscriptions: ProfileSubscriptionListItem[];
  subscriptionPauses: ProfileSubscriptionPause[];
  subscriptionFilter: number;
}

export const initialProfileSubcriptionStore: IProfileSubscriptionStore = {
  personId: null,
  lastCreatedSubscription: null,
  lastUpdatedSubscription: null,
  lastPatchedSubscription: null,
  lastRenewedSubscription: null,
  lastClosedSubscription: null,
  lastPausedSubscription: null,
  lastSwitchedSubscription: null,
  lastTransferredSubscription: null,
  currentSubscription: null,
  lastDeletedSubscriptionId: null,
  lastDeletedSubscriptionRenewalsId: null,
  subscriptions: [],
  subscriptionPauses: [],
  subscriptionFilter: 0,
};
export const ProfileSubscriptionStore = signalStore(
  { providedIn: "root" },
  withState<IProfileSubscriptionStore>(initialProfileSubcriptionStore),
  withMethods((
    store,
    profileSubscriptionService = inject(ProfileSubscriptionService),
    alertStore = inject(AlertStore),
    errorStore = inject(ErrorStore),
    selfUserStore = inject(SelfUserStore),
    router = inject(Router),
  ) => {
    const renewSubscription = rxMethod<RenewProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { lastRenewedSubscription: null, ...setPropLoading("renewSubscription") })),
      switchMap((payload) => {
        if (!payload?.SubscriptionId) {
          const error = new Error("Missing SubscriptionId for renew");
          patchState(store, { ...setPropError("renewSubscription", error) });
          errorStore.handleError({ error, title: "Não foi possível identificar a assinatura para renovar." });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaRenovar],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RENEW.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RENEW.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { lastRenewedSubscription: null, ...setPropInit("renewSubscription") });
              return EMPTY;
            }

            return profileSubscriptionService.renewSubscription(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "SUBSCRIPTION.ALERTS.RENEW.SUCCESSFFULY",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                    textPrimaryButton: 'SALES.SALE.ALERT.CREATE.TEXT_BUTTON',
                    actionButton: () => {
                      router.navigate(
                        ['profile/', response.PersonId, 'financial', 'bills-to-receive'],
                        {
                          queryParams: {
                            accountsReceivableId: response.Installments?.map(item => item.SubscriptionInstallmentId) ?? []
                          }
                        }
                      );
                    }
                  });
                  patchState(store, { lastRenewedSubscription: response, ...setPropLoaded("renewSubscription") });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("renewSubscription", error) });
                },
              })
            );
          }),
        );
      })
    ));

    const closeSubscription = rxMethod<CloseProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { lastClosedSubscription: null, ...setPropLoading("closeSubscription") })),
      switchMap((payload) => {
        if (!payload?.SubscriptionId) {
          const error = new Error("Missing SubscriptionId for close");
          patchState(store, { ...setPropError("closeSubscription", error) });
          errorStore.handleError({ error, title: "Não foi possível identificar a assinatura para encerrar." });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.EncerrarAssinatura],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_CLOSE.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_CLOSE.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { lastClosedSubscription: null, ...setPropInit("closeSubscription") });
              return EMPTY;
            }

            return profileSubscriptionService.closeSubscription(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "SUBSCRIPTION.ALERTS.CLOSE.SUCCESSFFULLY",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, { lastClosedSubscription: response, ...setPropLoaded("closeSubscription") });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("closeSubscription", error) });
                },
              })
            );
          }),
        );
      })
    ));

    const deleteSubscriptionById = rxMethod<string>(pipe(
      filter(Boolean),
      tap(() => patchState(store, { ...setPropLoading("deleteSubscriptionById") })),
      switchMap((subscriptionId) =>
        from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaRemover],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_DELETE.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_DELETE.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { ...setPropInit("deleteSubscriptionById") });
              return EMPTY;
            }

            return profileSubscriptionService.deleteSubscriptionById(subscriptionId).pipe(
              tapResponse({
                next: () => {
                  alertStore.create({
                    message: "SUBSCRIPTION.ALERTS.REMOVED.SUCCESSFFULLY",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, {
                    lastDeletedSubscriptionId: subscriptionId,
                    currentSubscription: null,
                    ...setPropLoaded("deleteSubscriptionById"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("deleteSubscriptionById", error) });
                },
              })
            );
          }),
        )
      )
    ));

    const deleteSubscriptionRenewals = rxMethod<string>(pipe(
      filter(Boolean),
      tap(() =>
        patchState(store, {
          lastDeletedSubscriptionRenewalsId: null,
          ...setPropLoading("deleteSubscriptionRenewals"),
        })
      ),
      switchMap((subscriptionId) =>
        from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaAlterar],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_CHANGE.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_CHANGE.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { ...setPropInit("deleteSubscriptionRenewals") });
              return EMPTY;
            }

            return profileSubscriptionService
              .deleteSubscriptionRenewals(subscriptionId)
              .pipe(
                tapResponse({
                  next: () => {
                    alertStore.create({
                      message: "Renovações excluídas com sucesso.",
                      title: "ALERT.TITLE_SUCCESS",
                      type: "success",
                    });
                    patchState(store, {
                      lastDeletedSubscriptionRenewalsId: subscriptionId,
                      ...setPropLoaded("deleteSubscriptionRenewals"),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError("deleteSubscriptionRenewals", error),
                    });
                  },
                })
              );
          }),
        )
      )
    ));

    type TGetSubscriptionsParams = { personId?: string | null; filter?: number | null };

    const getSubscriptionsByPerson = rxMethod<string | null | undefined | TGetSubscriptionsParams>(pipe(
      tap(() => patchState(store, { subscriptions: [], ...setPropLoading("getSubscriptionsByPerson") })),
      switchMap((params) => {
        const normalizedParams: TGetSubscriptionsParams =
          typeof params === "object" && params !== null && !Array.isArray(params)
            ? params
            : { personId: params as string | null | undefined };

        const personId = normalizedParams.personId || store.personId();

        if (!personId) {
          const error = new Error("Missing personId");
          patchState(store, { ...setPropError("getSubscriptionsByPerson", error) });
          errorStore.handleError({ error, title: "Não foi possível identificar a pessoa para listar assinaturas." });
          return EMPTY;
        }

        const filterValue =
          normalizedParams.filter ?? store.subscriptionFilter() ?? 0;

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaObterTodos],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_LIST.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_LIST.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { subscriptions: [], ...setPropInit("getSubscriptionsByPerson") });
              return EMPTY;
            }

            return profileSubscriptionService.getSubscriptionsByPerson(personId, filterValue).pipe(
              tapResponse({
                next: (subscriptions) => {
                  patchState(store, {
                    personId,
                    subscriptions,
                    subscriptionFilter: filterValue,
                    ...setPropLoaded("getSubscriptionsByPerson"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("getSubscriptionsByPerson", error) });
                },
              })
            );
          }),
        );
      })
    ));

    const getSubscriptionById = rxMethod<string | null | undefined>(pipe(
      tap(() =>
        patchState(store, {
          ...setPropLoading("getSubscriptionById"),
        })
      ),
      switchMap((subscriptionIdParam) => {
        const subscriptionId = subscriptionIdParam || store.currentSubscription()?.SubscriptionId || null;

        if (!subscriptionId) {
          const error = new Error("Missing subscriptionId");
          patchState(store, { ...setPropError("getSubscriptionById", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION" });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaObterPorId],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_VIEW.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_VIEW.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, {
                currentSubscription: null,
                subscriptionPauses: [],
                ...setPropInit("getSubscriptionById"),
              });
              return EMPTY;
            }

            return profileSubscriptionService.getSubscriptionById(subscriptionId).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    currentSubscription: response,
                    subscriptionPauses: response?.Pauses ?? [],
                    ...setPropLoaded("getSubscriptionById"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.LOAD_ERROR' });
                  patchState(store, { 
                    currentSubscription: null,
                    subscriptionPauses: [],
                    ...setPropError("getSubscriptionById", error)
                  });
                },
              })
            );
          }),
        );
      })
    ));

    const addSubscriptionPause = rxMethod<AddPauseProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { lastPausedSubscription: null, ...setPropLoading("addSubscriptionPause") })),
      switchMap((payload) =>
        from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaAdicionarTrancamento],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PAUSE.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PAUSE.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { lastPausedSubscription: null, ...setPropInit("addSubscriptionPause") });
              return EMPTY;
            }

            if (!payload?.SubscriptionId) {
              const error = new Error("Missing SubscriptionId for pause");
              patchState(store, { ...setPropError("addSubscriptionPause", error) });
              errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION" });
              return EMPTY;
            }

            if (!payload?.StartOnUtc) {
              const error = new Error("Missing StartOnUtc for pause");
              patchState(store, { ...setPropError("addSubscriptionPause", error) });
              errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_START_ON" });
              return EMPTY;
            }

            if (!payload?.Motivation) {
              const error = new Error("Missing Motivation for pause");
              patchState(store, { ...setPropError("addSubscriptionPause", error) });
              errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_REASON" });
              return EMPTY;
            }

            return profileSubscriptionService.addSubscriptionPause(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.ADD_SUCCESS",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, {
                    lastPausedSubscription: response,
                    currentSubscription: response,
                    subscriptionPauses: response?.Pauses ?? [],
                    ...setPropLoaded("addSubscriptionPause"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("addSubscriptionPause", error) });
                },
              })
            );
          }),
        )
      )
    ));

    const updateSubscriptionPause = rxMethod<UpdatePauseProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { ...setPropLoading("updateSubscriptionPause") })),
      switchMap((payload) => {
        if (!payload?.SubscriptionId) {
          const error = new Error("Missing SubscriptionId for pause");
          patchState(store, { ...setPropError("updateSubscriptionPause", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION" });
          return EMPTY;
        }

        if (!payload?.SubscriptionPauseId) {
          const error = new Error("Missing SubscriptionPauseId for pause");
          patchState(store, { ...setPropError("updateSubscriptionPause", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_PAUSE" });
          return EMPTY;
        }

        if (!payload?.StartOnUtc) {
          const error = new Error("Missing StartOnUtc for pause");
          patchState(store, { ...setPropError("updateSubscriptionPause", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_START_ON" });
          return EMPTY;
        }

        if (!payload?.Motivation) {
          const error = new Error("Missing Motivation for pause");
          patchState(store, { ...setPropError("updateSubscriptionPause", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_REASON" });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaEditarTrancamento],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_EDIT.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_EDIT.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { ...setPropInit("updateSubscriptionPause") });
              return EMPTY;
            }

            return profileSubscriptionService.updateSubscriptionPause(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.UPDATE_SUCCESS",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, {
                    lastPausedSubscription: response,
                    currentSubscription: response,
                    subscriptionPauses: response?.Pauses ?? [],
                    ...setPropLoaded("updateSubscriptionPause"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("updateSubscriptionPause", error) });
                },
              })
            );
          }),
        );
      })
    ));

    const deleteSubscriptionPause = rxMethod<DeletePauseProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { ...setPropLoading("deleteSubscriptionPause") })),
      switchMap((payload) => {
        if (!payload?.SubscriptionId) {
          const error = new Error("Missing SubscriptionId for pause");
          patchState(store, { ...setPropError("deleteSubscriptionPause", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION" });
          return EMPTY;
        }

        if (!payload?.SubscriptionPauseId) {
          const error = new Error("Missing SubscriptionPauseId for pause");
          patchState(store, { ...setPropError("deleteSubscriptionPause", error) });
          errorStore.handleError({ error, title: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_PAUSE" });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaRemoverTrancamento],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_DELETE.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_DELETE.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { ...setPropInit("deleteSubscriptionPause") });
              return EMPTY;
            }

            return profileSubscriptionService.deleteSubscriptionPause(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "PROFILE.SUBSCRIPTION.PAUSES.ALERTS.DELETE_SUCCESS",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, {
                    lastPausedSubscription: response,
                    currentSubscription: response,
                    subscriptionPauses: response?.Pauses ?? [],
                    ...setPropLoaded("deleteSubscriptionPause"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("deleteSubscriptionPause", error) });
                },
              })
            );
          }),
        );
      })
    ));

    const switchSubscription = rxMethod<SwitchProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { lastSwitchedSubscription: null, ...setPropLoading("switchSubscription") })),
      switchMap((payload) => {
        if (!payload?.subscriptionId) {
          const error = new Error("Missing subscriptionId for switch");
          patchState(store, { ...setPropError("switchSubscription", error)});
          errorStore.handleError({ error, title: "Não foi possível identificar a assinatura para transferir." });
          return EMPTY;
        }

        if (!payload?.destinationPersonId) {
          const error = new Error("Missing destinationPersonId for switch");
          patchState(store, { ...setPropError("switchSubscription", error) });
          errorStore.handleError({ error, title: "Informe a pessoa de destino para a transferência." });
          return EMPTY;
        }

        if (!payload?.destinationCycleId) {
          const error = new Error("Missing destinationCycleId for switch");
          patchState(store, { ...setPropError("switchSubscription", error) });
          errorStore.handleError({ error, title: "Informe o ciclo de destino para a transferência." });
          return EMPTY;
        }

        if (!payload?.transferOnUtc) {
          const error = new Error("Missing transferOnUtc for switch");
          patchState(store, { ...setPropError("switchSubscription", error) });
          errorStore.handleError({ error, title: "Informe a data da transferência." });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AssinaturaPessoaAlterar],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_CHANGE.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_CHANGE.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { lastSwitchedSubscription: null, ...setPropInit("switchSubscription") });
              return EMPTY;
            }

            return profileSubscriptionService.switchSubscription(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "SUBSCRIPTION.ALERTS.SWAPPED.SUCCESSFFULY",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, { lastSwitchedSubscription: response, ...setPropLoaded("switchSubscription") });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("switchSubscription", error) });
                },
              })
            );
          }),
        );
      })
    ));

    const transferSubscription = rxMethod<TransferProfileSubscriptionPayload>(pipe(
      tap(() => patchState(store, { lastTransferredSubscription: null, ...setPropLoading("transferSubscription") })),
      switchMap((payload) => {
        if (!payload?.SubscriptionId) {
          const error = new Error("Missing SubscriptionId for transfer");
          patchState(store, { ...setPropError("transferSubscription", error) });
          errorStore.handleError({ error, title: "Não foi possível identificar a assinatura para transferir." });
          return EMPTY;
        }

        if (!payload?.DestinationPersonId) {
          const error = new Error("Missing DestinationPersonId for transfer");
          patchState(store, { ...setPropError("transferSubscription", error) });
          errorStore.handleError({ error, title: "Informe a pessoa de destino para a transferência." });
          return EMPTY;
        }

        if (!payload?.DestinationCycleId) {
          const error = new Error("Missing DestinationCycleId for transfer");
          patchState(store, { ...setPropError("transferSubscription", error) });
          errorStore.handleError({ error, title: "Informe o ciclo de destino para a transferência." });
          return EMPTY;
        }

        if (!payload?.TransferOnUtc) {
          const error = new Error("Missing TransferOnUtc for transfer");
          patchState(store, { ...setPropError("transferSubscription", error) });
          errorStore.handleError({ error, title: "Informe a data da transferência." });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AdicionarAssinaturaPessoaTransferencia],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_TRANSFER_ADD.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_TRANSFER_ADD.MESSAGE`,
            },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, { lastTransferredSubscription: null, ...setPropInit("transferSubscription") });
              return EMPTY;
            }

            return profileSubscriptionService.transferSubscription(payload).pipe(
              tapResponse({
                next: (response) => {
                  alertStore.create({
                    message: "SUBSCRIPTION.ALERTS.TRANSFERRED.SUCCESSFFULY",
                    title: "ALERT.TITLE_SUCCESS",
                    type: "success",
                  });
                  patchState(store, { lastTransferredSubscription: response, ...setPropLoaded("transferSubscription") });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError("transferSubscription", error) });
                },
              })
            );
          }),
        );
      })
    ));

    return {
      setPersonId: (personId: string | null) => {
        patchState(store, { personId });
      },
      clearRenewedSubscription: () => {
        patchState(store, { lastRenewedSubscription: null }, setPropInit("renewSubscription"));
      },
      clearClosedSubscription: () => {
        patchState(store, { lastClosedSubscription: null }, setPropInit("closeSubscription"));
      },
      clearPausedSubscription: () => {
        patchState(
          store,
          { lastPausedSubscription: null },
        );
      },
      clearSwitchedSubscription: () => {
        patchState(store, { lastSwitchedSubscription: null }, setPropInit("switchSubscription"));
      },
      clearTransferredSubscription: () => {
        patchState(store, { lastTransferredSubscription: null }, setPropInit("transferSubscription"));
      },
      clearCurrentSubscription: () => {
        patchState(
          store,
          { currentSubscription: null, subscriptionPauses: [] },
          setPropInit("getSubscriptionById")
        );
      },
      clearDeletedSubscription: () => {
        patchState(store, { lastDeletedSubscriptionId: null }, setPropInit("deleteSubscriptionById"));
      },
      clearDeletedSubscriptionRenewals: () => {
        patchState(
          store,
          { lastDeletedSubscriptionRenewalsId: null },
          setPropInit("deleteSubscriptionRenewals"),
        );
      },
      clearSubscriptionsByPerson: () => {
        patchState(
          store,
          { subscriptions: [] },
          setPropInit("getSubscriptionsByPerson"),
        );
      },
      initProps: (...props: string[]) => {
        patchState(store, { ...setPropInit(...props) });
      },
      setPropsInit(props: string[]): void {
        patchState(store, setPropInit(...props));
      },
      renewSubscription,
      closeSubscription,
      getSubscriptionById,
      addSubscriptionPause,
      updateSubscriptionPause,
      deleteSubscriptionPause,
      switchSubscription,
      transferSubscription,
      deleteSubscriptionById,
      deleteSubscriptionRenewals,
      getSubscriptionsByPerson,
    };
  }),
  withReqState(
    "renewSubscription",
    "closeSubscription",
    "addSubscriptionPause",
    "updateSubscriptionPause",
    "deleteSubscriptionPause",
    "switchSubscription",
    "transferSubscription",
    "getSubscriptionById",
    "deleteSubscriptionById",
    "deleteSubscriptionRenewals",
    "getSubscriptionsByPerson",
  )
);
